/**
 * PreloadContext (iter 135) — global cache that warms the entire account
 * on login and keeps every tab feeling instant.
 *
 * Strategy:
 *   • On mount, hit /api/preload once → cache result in memory
 *   • Auto-refresh every 30s in the background (silent)
 *   • Manually `refresh()` if a tab wants the freshest data
 *   • Also warms /tea news in the background so the news tab loads instantly
 *   • Polls /api/market/news/alerts/scan every 90s and fires a Sonner toast
 *     for any article with impact_score ≥ 80 we haven't surfaced yet.
 *
 * Tabs use: `const { preload, tea, refresh, lastUpdated } = usePreload()`
 *   - render from cache immediately → no spinners
 *   - the cache is null only on first paint after login (≤1s typically)
 */
import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { fastGetAsync, prewarm } from "../lib/fastFetch";
import { useAuth } from "../context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PreloadCtx = createContext({
  preload: null, tea: null, refresh: () => {}, lastUpdated: null, highImpact: [],
});

// Persisted set of article IDs we've already toasted on this device, so refreshes
// don't spam the user with the same alert again.
const ALERT_STORE_KEY = "omega.newsAlerts.seen.v1";
function loadSeenAlerts() {
  try { return new Set(JSON.parse(localStorage.getItem(ALERT_STORE_KEY) || "[]")); }
  catch { return new Set(); }
}
function saveSeenAlerts(set) {
  try {
    // Cap to last 200 IDs so localStorage doesn't grow forever
    const arr = Array.from(set).slice(-200);
    localStorage.setItem(ALERT_STORE_KEY, JSON.stringify(arr));
  } catch { /* ignore quota */ }
}

export function PreloadProvider({ children }) {
  // PreloadProvider is mounted at the top of the tree (inside AuthProvider)
  // regardless of auth state, so it renders on the very first paint — before
  // AuthContext's own GET /auth/me has resolved and before the login cookie
  // exists. Everything below must wait for a confirmed `user` before firing
  // a single request, or every prewarm/poll call 401s once at boot and (bar
  // /preload's own 30s self-heal) is never retried for the life of the tab.
  const { user, loading: authLoading } = useAuth();
  const ready = !authLoading && !!user;
  const [preload, setPreload] = useState(null);
  const [tea, setTea] = useState(null);
  const [highImpact, setHighImpact] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const aliveRef = useRef(true);
  const seenAlertsRef = useRef(loadSeenAlerts());

  const refresh = useCallback(async () => {
    try {
      // iter 150 — SWR cache for /api/preload: shows last-good payload instantly,
      // refreshes in background. First load is still network-bound; every
      // subsequent tab-switch hydrates in <10 ms.
      const { data, fromCache } = await fastGetAsync(`${API}/preload`);
      if (aliveRef.current) {
        setPreload(data);
        setLastUpdated(new Date());
        // If the cache served us, also fire an immediate revalidation so the
        // user sees the freshest snapshot quickly.
        if (fromCache) {
          fastGetAsync(`${API}/preload`, { force: true })
            .then(({ data: fresh }) => {
              if (aliveRef.current) {
                setPreload(fresh);
                setLastUpdated(new Date());
              }
            })
            .catch(() => {});
        }
      }
    } catch {
      /* keep stale data — UI keeps working */
    }
  }, []);

  // Pre-warm every panel's key endpoints once the user is actually
  // authenticated, so every tab opens with data already in localStorage —
  // no blank states, no spinners, and no wasted 401s while logged out.
  useEffect(() => {
    if (!ready) return;
    prewarm([
      // Canon / doctrine
      `${API}/omega/glossary`,
      `${API}/omega/known-limits`,
      `${API}/omega/sensing/overview`,
      `${API}/omega/calibration`,
      `${API}/omega/growth-arc`,
      // Autopilot panel
      `${API}/autopilot/performance`,
      `${API}/autopilot/positions`,
      `${API}/autopilot/decisions?limit=30`,
      `${API}/pdt/status`,
      // Autopilot expanded modal
      `${API}/omega/status`,
      `${API}/autopilot/paper-stats`,
      `${API}/settings`,
      // Risk dashboard
      `${API}/omega/kill-switches`,
      `${API}/risk/dashboard`,
      // Analytics
      `${API}/autopilot/outcomes?limit=20`,
    ]);
  }, [ready]);

  const warmTea = useCallback(async () => {
    try {
      // Pull a smaller, faster set just to populate the cache; insight=false on
      // first warm so Yahoo+Finnhub return quickly (≤4s), then a background pass
      // re-queries with insight=true to fill in Omega's Takes.
      const fast = await Promise.all(
        ["SPY", "AAPL", "NVDA", "TSLA"].map(t =>
          axios.get(`${API}/market/news/v2?symbol=${t}&limit=4&insight=false`)
            .then(r => r.data?.articles || [])
            .catch(() => [])
        )
      );
      if (!aliveRef.current) return;
      const flat = [].concat(...fast);
      const seen = new Map();
      for (const a of flat) if (!seen.has(a.id)) seen.set(a.id, a);
      setTea(Array.from(seen.values()).sort((a, b) =>
        (b.publishedAt || "").localeCompare(a.publishedAt || "")
      ));
      // Then upgrade with insights in background (silent)
      const rich = await Promise.all(
        ["SPY", "AAPL", "NVDA", "TSLA"].map(t =>
          axios.get(`${API}/market/news/v2?symbol=${t}&limit=4&insight=true`)
            .then(r => r.data?.articles || [])
            .catch(() => [])
        )
      );
      if (!aliveRef.current) return;
      const flatRich = [].concat(...rich);
      const seenRich = new Map();
      for (const a of flatRich) if (!seenRich.has(a.id)) seenRich.set(a.id, a);
      setTea(Array.from(seenRich.values()).sort((a, b) =>
        (b.publishedAt || "").localeCompare(a.publishedAt || "")
      ));
    } catch { /* silent */ }
  }, []);

  // High-impact news alerts: scan backend every 90s, toast on first sighting.
  const scanAlerts = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/market/news/alerts/scan`);
      if (!aliveRef.current) return;
      const matches = Array.isArray(r.data?.matches) ? r.data.matches : [];
      if (!matches.length) return;
      setHighImpact(matches);

      const fresh = matches.filter(m => m && m.id && !seenAlertsRef.current.has(m.id));
      if (!fresh.length) return;

      // Surface up to 3 toasts per scan so we don't carpet-bomb the user.
      for (const m of fresh.slice(0, 3)) {
        seenAlertsRef.current.add(m.id);
        const score = Number(m.impact_score) || 0;
        const ticker = (m.ticker || "").toUpperCase();
        const title = (m.title || "").slice(0, 110);
        toast(`⚠ Omega Alert · ${ticker} · ${score}/100`, {
          description: title,
          duration: 12000,
          action: {
            label: "Open",
            onClick: () => {
              if (m.url) window.open(m.url, "_blank", "noopener,noreferrer");
              else window.location.href = "/tea";
            },
          },
        });
      }
      // Mark remaining as seen too so we don't toast them on the next scan
      for (const m of fresh.slice(3)) seenAlertsRef.current.add(m.id);
      saveSeenAlerts(seenAlertsRef.current);
    } catch {
      /* silent — alerts are best-effort */
    }
  }, []);

  useEffect(() => {
    if (!ready) return;
    aliveRef.current = true;
    refresh();
    warmTea();
    // First alert scan a few seconds after mount so /preload and /tea win the
    // network race on first paint.
    const firstScan = setTimeout(scanAlerts, 4000);
    const id = setInterval(refresh, 30000);
    const alertId = setInterval(scanAlerts, 90000);
    return () => {
      aliveRef.current = false;
      clearTimeout(firstScan);
      clearInterval(id);
      clearInterval(alertId);
    };
  }, [ready, refresh, warmTea, scanAlerts]);

  return (
    <PreloadCtx.Provider value={{ preload, tea, highImpact, refresh, lastUpdated }}>
      {children}
    </PreloadCtx.Provider>
  );
}

export function usePreload() {
  return useContext(PreloadCtx);
}
